import { createEntityAdapter, createSlice, EntityState, PayloadAction } from "@reduxjs/toolkit";
import { ChatMember, ChatMessage, Chatter, Conversation, nebulaChatApi } from "../../services/nebulaChat";
import { MemberReadStatus, UserTyping } from "../../services/NebulaChatSocket";
import { chatApi } from "../../services/chatApi";
import { parseNumber } from "../authSlice";

export const nebulaChatsAdapter = createEntityAdapter<Conversation>({
    selectId: (chat) => chat.uuid,
    sortComparer: (a, b) => {
        if (a.is_favourite && !b.is_favourite) return -1;
        if (!a.is_favourite && b.is_favourite) return 1;
        return (b.last_message_at || b.created_at || "").localeCompare(a.last_message_at || a.created_at || "");
    }
})

export interface NebulaChatState {
    // userUuid: string;
    chats: EntityState<Conversation>;
    chat_members: Record<string, ChatMember[]>;
    extra_chatters: Record<string, Chatter>;
    active_conversation: string | undefined;
    typing_users: Record<string, string[]>;
    destination_conversations: Record<string, string>;
    // typingStatuses: Record<string, INotifyMessageStatus[]>
    // failedMessagesQueue: Record<string, IChatMessage[]>
    // messageReactions: Record<string, IMessageReaction>;
    // messageReplies: Record<string, { message: IChatMessage; sender: string } | undefined>;
    // externalReplies: IChatMessage[];
    // groupCreationActive: boolean;
    // usersForGroupSelection: IUserForChannel[];
    // inProgressTextInputs?: Record<string, string>
    // searchResults?: Record<string, IChatMessage[] | undefined>;
    // scrollToMessageId?: string;
    // scrollToThreadMessageId?: string;
    // focusAttachment: FocusMessageAttachment | undefined;
    // lastUpdatedChatId: string;
}

const initialState: NebulaChatState = {
    // userUuid: '',
    chats: nebulaChatsAdapter.getInitialState(),
    chat_members: {},
    extra_chatters: {},
    active_conversation: undefined,
    typing_users: {},
    destination_conversations: {},
    // typingStatuses: {},
    // failedMessagesQueue: {},
    // messageReplies: {},
    // externalReplies: [],
    // messageReactions: {},
    // groupCreationActive: false,
    // usersForGroupSelection: [],
    // lastUpdatedChatId: '',
    // focusAttachment: undefined
};

export const chatNSlice = createSlice({
    name: 'chatN',
    initialState,
    reducers: {
        updateOneConversation: (state, { payload }: PayloadAction<{ chat_uuid: string, changes: Partial<Conversation> }>) => {
            nebulaChatsAdapter.updateOne(state.chats, {
                id: payload.chat_uuid,
                changes: { ...payload.changes }
            })
        },
        setActiveConversation: (state, { payload }: PayloadAction<string | undefined>) => ({
            ...state,
            active_conversation: payload
        }),
        updateTypingUser: (state, { payload }: PayloadAction<UserTyping>) => {
            const curr = state.typing_users[payload.to] ?? [];
            const userIdx = curr.indexOf(payload.user);
            if (
                (payload.is_typing && userIdx > -1)
                || (!payload.is_typing && userIdx < 0)
            ) {
                return state;
            }

            if (payload.is_typing) {
                return {
                    ...state,
                    typing_users: {
                        ...state.typing_users,
                        [payload.to]: [...curr, payload.user]
                    }
                }
            }

            const newList = curr.filter(u => u !== payload.user);
            return {
                ...state,
                typing_users: {
                    ...state.typing_users,
                    [payload.to]: newList
                }
            };
        },
        receivedConversationMessage: (state, { payload: { message, unread } }: PayloadAction<{ message: ChatMessage; unread: number }>) => {
            nebulaChatsAdapter.updateOne(state.chats, {
                id: message.to,
                changes: {
                    last_message: message.content ?? undefined,
                    last_message_at: message.time,
                    last_message_from: message.from,
                    last_message_uuid: message.uuid,
                    message,
                    unread,
                },
            });
        },
        markConversationRead: (state, { payload: { conversationUuid } }: PayloadAction<{ conversationUuid: string }>) => {
            nebulaChatsAdapter.updateOne(state.chats, {
                id: conversationUuid,
                changes: {
                    unread: 0,
                },
            });
        },
        removeConversation: (state, { payload: { conversationUuid } }: PayloadAction<{ conversationUuid: string }>) => {
            nebulaChatsAdapter.removeOne(state.chats, conversationUuid);
        },
        setOneConversation: (state, { payload: { conversation } }: PayloadAction<{ conversation: Conversation }>) => {
            nebulaChatsAdapter.setOne(state.chats, conversation);
        },
        updateMemberRead: (state, { payload }: PayloadAction<MemberReadStatus>) => {
            const chatMembers = state.chat_members[payload.to];
            if (!chatMembers) return;

            const member = chatMembers.find(m => m.member === payload.user);
            if (member) {
                if (!member.last_delivered_at || (payload.last_delivered_at && (member.last_delivered_at < payload.last_delivered_at))) {
                    member.last_delivered_at = payload.last_delivered_at;
                }
                if (!member.last_read_at || (payload.last_read_at && (member.last_read_at < payload.last_read_at))) {
                    member.last_read_at = payload.last_read_at;
                }
            } else {
                chatMembers.push({
                    uuid: payload.user,
                    member: payload.user,
                    external: false,
                    joined_at: "9999",
                    last_delivered_at: payload.last_delivered_at,
                    last_read_at: payload.last_read_at,
                    hidden: true,
                });
            }
        },
        setChatParticipants: (state, { payload }: PayloadAction<{ chat_uuid: string, participants: ChatMember[] }>) => {
            // eslint-disable-next-line no-param-reassign
            state.chat_members[payload.chat_uuid] = payload.participants;
        },
        updateLocalChatMember: (state, { payload: { to, is_archived, is_favourite, is_muted } }: PayloadAction<{ to: string, is_favourite: boolean, is_muted: boolean, is_archived: boolean }>) => {
            nebulaChatsAdapter.updateOne(state.chats, {
                id: to,
                changes: {
                    is_archived,
                    is_favourite,
                    is_muted
                },
            });
        },
    },
    extraReducers: builder => {
        builder.addMatcher(nebulaChatApi.endpoints.getLastMessagesN.matchFulfilled, (state, { payload }) => {
            nebulaChatsAdapter.upsertMany(state.chats, payload.conversations);
        });
        builder.addMatcher(nebulaChatApi.endpoints.getChatMembers.matchFulfilled, (state, { payload, meta: { arg: { originalArgs: { chat_uuid } } } }) => ({
            ...state,
            chat_members: {
                ...state.chat_members,
                [chat_uuid]: payload,
            }
        }));
        builder.addMatcher(nebulaChatApi.endpoints.getChatFriendDetails.matchFulfilled, (state, { payload }) => {
            const extras = Object.fromEntries(payload.map(f => [f.uuid, f]))

            return {
                ...state,
                extra_chatters: {
                    ...state.extra_chatters,
                    ...extras,
                }
            }
        });
        builder.addMatcher(nebulaChatApi.endpoints.getChatUserDetails.matchFulfilled, (state, { payload }) => ({
            ...state,
            extra_chatters: {
                ...state.extra_chatters,
                [payload.uuid]: payload
            }
        }));
        builder.addMatcher(nebulaChatApi.endpoints.createChannelN.matchFulfilled, (state, { payload }) => {
            if (payload) {
                nebulaChatsAdapter.setOne(state.chats, payload);
                // eslint-disable-next-line no-param-reassign
                state.active_conversation = payload.uuid;
            }
        });
        builder.addMatcher(nebulaChatApi.endpoints.getChatIntegrationDetails.matchFulfilled, (state, { payload }) => {
            payload.forEach(i => {
                // eslint-disable-next-line no-param-reassign
                state.extra_chatters[i.uuid] = {
                    uuid: i.uuid,
                    name: i.nickname || "",
                    is_active: true,
                    file_key: null,
                    is_integration: true
                };
                // eslint-disable-next-line no-param-reassign
                state.destination_conversations[i.destination] = i.uuid;
            })
        });
        builder.addMatcher(chatApi.endpoints.postIntegration.matchFulfilled, (state, { payload, meta: { arg: { originalArgs: { destination } } } }) => {
            const phoneNumber = parseNumber(destination);

            // eslint-disable-next-line no-param-reassign
            state.destination_conversations[phoneNumber] = payload.result.uuid;
        })
    }
});

export const chatN = chatNSlice.reducer;
